import { useEffect, useState } from "react";
import dayjs from "dayjs";
import sessionHandling from "services/utils/notificationUtils";
import { setCachedPayInputConfig } from "pages/PayRoll/Configuration/PayInputConfiguration/payInputConfigApi";

// Cache key for the reimbursement settings in the shared pay-input config
// cache (read by the sidebar to hide Pay Inputs > Reimbursements).
export const REIMBURSEMENT_PAY_INPUT_TYPE = "REIMBURSEMENT";

// API calls for the four Reimbursement screens:
//   Payroll > Configuration > Pay Input > Reimbursements
//     /api/payroll/configuration/pay-input/reimbursement/ (settings, types, pay-components)
//   My Workspace > My Requests > Reimbursement
//     /api/expense/ (own claims, submit, cancel) + /api/expense-attachment/{id}/
//   Team Workspace > Approvals > Reimbursement
//     /api/team_zone/approve/expense/teamworkspace-expense-approve/ (list, review)
//   Payroll > Pay Inputs > Reimbursements (HR)
//     /api/payroll/pay-input/reimbursement/ (summary, claims, import), per
//     Reimbursement_PayInputs_API.pdf (5 Nov 2026, proposed contract)
// Claims come from the old expense tables. Fields the backend does not send
// yet (bill_number, pay_month, added_to_payroll_at, paid_at, amount_approved)
// are read when present, so the screens fill in once the backend adds them.

const CONFIG_BASE = "payroll/configuration/pay-input/reimbursement/";
const TEAM_CLAIMS = "team_zone/approve/expense/teamworkspace-expense-approve/";
const PAY_INPUT_BASE = "payroll/pay-input/reimbursement/";

// 409 means "logged in elsewhere" on this server, except these rule codes.
const CONFLICT_CODES = new Set(["TYPE_IN_USE", "NOT_EDITABLE", "NOT_CANCELLABLE"]);

export class ReimbursementApiError extends Error {
  constructor(message, { status, code, fieldErrors } = {}) {
    super(message);
    this.status = status;
    this.code = code;
    this.fieldErrors = fieldErrors || {};
  }
}

const textOf = (value) => {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.map(textOf).filter(Boolean).join(" ");
  if (typeof value === "object") return Object.values(value).map(textOf).filter(Boolean).join(" ");
  return String(value);
};

// Reads both error formats: the new config API ({code, message, errors:[...],
// meta.detail}) and the old claim APIs ({error}, {detail} or DRF field errors).
const parseError = (body, status) => {
  const fieldErrors = {};
  let message = "";
  let code = body?.code;
  if (Array.isArray(body?.errors)) {
    const messages = [];
    body.errors.forEach((item) => {
      code = code || item?.code;
      const detail = item?.meta?.detail;
      if (detail && typeof detail === "object") {
        Object.entries(detail).forEach(([field, value]) => { fieldErrors[field] = textOf(value); });
      }
      if (item?.field) fieldErrors[item.field] = item.message;
      if (item?.message) messages.push(item.message);
    });
    const detailText = Object.values(fieldErrors).join(" ");
    message = messages.filter((text) => text !== "Check the highlighted fields.").join(" ") || detailText || messages.join(" ");
  } else if (body?.error || body?.message || body?.detail) {
    message = textOf(body.error || body.message || body.detail);
  } else if (body && typeof body === "object") {
    Object.entries(body).forEach(([field, value]) => { fieldErrors[field] = textOf(value); });
    message = Object.values(fieldErrors).join(" ");
  }
  return new ReimbursementApiError(message || `Request failed (${status}). Please try again.`, { status, code, fieldErrors });
};

const buildUrl = (domain, path, query) => {
  const params = new URLSearchParams();
  Object.entries(query || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") params.append(key, value);
  });
  const search = params.toString();
  return `${String(domain || "").replace(/\/$/, "")}/${path}${search ? `?${search}` : ""}`;
};

const request = async (domain, path, { method = "GET", query, body, form, signal } = {}) => {
  const headers = { Authorization: `Token ${localStorage.getItem("token")}` };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  const response = await fetch(buildUrl(domain, path, query), {
    method,
    signal,
    headers,
    ...(form ? { body: form } : body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (response.status === 401) {
    localStorage.clear();
    window.location.href = "/auth/login";
    throw new ReimbursementApiError("Your session has expired. Please sign in again.", { status: 401 });
  }
  if (response.status === 204) return null;
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const error = parseError(data, response.status);
    if (response.status === 409 && !CONFLICT_CODES.has(error.code)) sessionHandling();
    throw error;
  }
  return data;
};

const toNumber = (value) => (value === null || value === undefined || value === "" ? null : Number(value));
const toAmountString = (value) => (value === null || value === undefined || value === "" ? null : Number(value).toFixed(2));

// ---------- Configuration: settings ----------

export const fromApiSettings = (data) => ({
  is_configured: Boolean(data.is_configured),
  enabled: data.is_enabled !== false,
  treatment: data.default_treatment,
  treatmentOptions: data.treatment_options || [],
  approvalRequired: data.approval_required ? "yes" : "no",
  approvalConfigurable: true,
  billUploadEnabled: data.bill_upload_enabled !== false,
  sourceOptions: (data.source_details || []).map((source) => ({
    value: source.source_type,
    label: source.label,
    comingSoon: !source.is_available || source.source_type === "ERP",
  })),
  sources: (data.source_details || [])
    .filter((source) => source.is_available && source.source_type !== "ERP" && data.allowed_sources?.[source.source_type])
    .map((source) => source.source_type),
});

// Same call shape as requestPayInputConfig, so the Pay Input screen can use
// either: GET without `config`, PUT with it. Bill upload and the HR role are
// not on the screen, so they are left out of the PUT and keep their values.
export const requestReimbursementSettings = async ({ domain, companyId, config, signal }) => {
  if (!config) {
    const data = await request(domain, `${CONFIG_BASE}settings/`, { query: { company_id: companyId }, signal });
    const loaded = fromApiSettings(data);
    setCachedPayInputConfig(domain, companyId, REIMBURSEMENT_PAY_INPUT_TYPE, loaded);
    return { config: loaded };
  }
  const result = await request(domain, `${CONFIG_BASE}settings/`, {
    method: "PUT",
    signal,
    body: {
      company_id: Number(companyId),
      is_enabled: Boolean(config.enabled),
      default_treatment: config.treatment,
      approval_required: config.approvalRequired === "yes",
      allowed_sources: Object.fromEntries(config.sourceOptions.map(({ value, comingSoon }) => [
        value, !comingSoon && value !== "ERP" && config.sources.includes(value),
      ])),
    },
  });
  const saved = fromApiSettings(result?.data || {});
  setCachedPayInputConfig(domain, companyId, REIMBURSEMENT_PAY_INPUT_TYPE, saved);
  return { config: saved, message: result?.message };
};

// ---------- Configuration: types and payslip lines ----------

export const fromApiType = (item) => ({
  id: item.id,
  name: item.name || "",
  helpText: item.help_text || "",
  limitPerClaim: toNumber(item.limit_per_claim),
  limitPerMonth: toNumber(item.limit_per_month),
  billWithinDays: toNumber(item.bill_within_days),
  billNeeded: Boolean(item.bill_required),
  payComponentId: item.pay_component_id ?? "",
  payslipLine: item.pay_component_name || "",
  active: Boolean(item.is_active),
  used: Boolean(item.in_use),
  hint: item.hint || "",
  sortOrder: item.sort_order ?? 0,
});

const toApiType = (values) => ({
  name: values.name,
  help_text: values.helpText || null,
  limit_per_claim: toAmountString(values.limitPerClaim),
  limit_per_month: toAmountString(values.limitPerMonth),
  bill_within_days: Number(values.billWithinDays),
  bill_required: Boolean(values.billNeeded),
  pay_component_id: Number(values.payComponentId),
  is_active: Boolean(values.active),
});

export const fetchReimbursementTypes = async ({ domain, companyId, activeOnly = false, signal }) => {
  const data = await request(domain, `${CONFIG_BASE}types/`, {
    query: { company_id: companyId, page_size: 200, ...(activeOnly ? { is_active: "true" } : {}) },
    signal,
  });
  const rows = Array.isArray(data) ? data : data?.results || [];
  return rows.map(fromApiType);
};

export const createReimbursementType = async ({ domain, companyId, values }) => {
  const result = await request(domain, `${CONFIG_BASE}types/`, {
    method: "POST",
    body: { company_id: Number(companyId), ...toApiType(values) },
  });
  return { type: fromApiType(result?.data || {}), message: result?.message };
};

export const updateReimbursementType = async ({ domain, id, values }) => {
  const result = await request(domain, `${CONFIG_BASE}types/${id}/`, { method: "PATCH", body: toApiType(values) });
  return { type: fromApiType(result?.data || {}), message: result?.message };
};

export const deleteReimbursementType = ({ domain, id }) =>
  request(domain, `${CONFIG_BASE}types/${id}/`, { method: "DELETE" });

export const fetchPayslipLines = async ({ domain, companyId, signal }) => {
  const data = await request(domain, `${CONFIG_BASE}pay-components/`, { query: { company_id: companyId }, signal });
  return (Array.isArray(data) ? data : data?.results || []).map((item) => ({
    value: item.id,
    label: item.payslip_name || item.name,
  }));
};

// Active types for the claim forms (same table as the claims, so the ids
// work for submit). Kept per company for the session.
const activeTypesCache = new Map();
export const useActiveReimbursementTypes = (domain, companyId) => {
  const key = `${domain}|${companyId}`;
  const validCompany = Number(companyId) > 0;
  const [state, setState] = useState(() => ({ key, types: activeTypesCache.get(key) || [], loading: false, error: "" }));
  useEffect(() => {
    if (!domain || !validCompany) {
      setState({ key, types: [], loading: false, error: "" });
      return undefined;
    }
    const controller = new AbortController();
    setState({ key, types: activeTypesCache.get(key) || [], loading: !activeTypesCache.has(key), error: "" });
    fetchReimbursementTypes({ domain, companyId, activeOnly: true, signal: controller.signal })
      .then((types) => {
        activeTypesCache.set(key, types);
        if (!controller.signal.aborted) setState({ key, types, loading: false, error: "" });
      })
      .catch((error) => {
        if (!controller.signal.aborted) setState({ key, types: activeTypesCache.get(key) || [], loading: false, error: error.message });
      });
    return () => controller.abort();
  }, [domain, companyId, key, validCompany]);
  return state.key === key ? state : { key, types: [], loading: true, error: "" };
};

// Switches from the reimbursement settings: Enable Reimbursements and bill
// photo upload (both default on, and stay on if the settings can't be
// loaded). Login is enough to read them.
export const useReimbursementSettings = (domain, companyId) => {
  const [flags, setFlags] = useState({ enabled: true, billUploadEnabled: true });
  useEffect(() => {
    if (!domain || !(Number(companyId) > 0)) return undefined;
    const controller = new AbortController();
    requestReimbursementSettings({ domain, companyId, signal: controller.signal })
      .then(({ config }) => {
        if (!controller.signal.aborted) setFlags({ enabled: config.enabled, billUploadEnabled: config.billUploadEnabled });
      })
      .catch(() => {});
    return () => controller.abort();
  }, [domain, companyId]);
  return flags;
};

export const useReimbursementBillUpload = (domain, companyId) =>
  useReimbursementSettings(domain, companyId).billUploadEnabled;

// ---------- Claims ----------

// "03-11-2026" or "03-11-2026, 10:42" -> "2026-11-03" / "2026-11-03 10:42".
const parseApiDate = (value) => {
  if (!value) return null;
  const match = String(value).match(/^(\d{2})-(\d{2})-(\d{4})(?:,?\s*(\d{2}):(\d{2}))?/);
  if (match) {
    const [, dd, mm, yyyy, hh, min] = match;
    return hh ? `${yyyy}-${mm}-${dd} ${hh}:${min}` : `${yyyy}-${mm}-${dd}`;
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD HH:mm") : null;
};

const parseDay = (value) => {
  const parsed = parseApiDate(value);
  return parsed ? parsed.slice(0, 10) : null;
};

// pay_month may come as "2026-11", "2026-11-01" or "Nov 2026".
const parsePayMonth = (value) => {
  if (!value) return null;
  const text = String(value);
  if (/^\d{4}-\d{2}/.test(text)) return text.slice(0, 7);
  const parsed = dayjs(`01 ${text}`);
  return parsed.isValid() ? parsed.format("YYYY-MM") : null;
};

export const claimRequestId = (id) => `RB-${String(id).padStart(5, "0")}`;

export const fromApiClaim = (item, { self, types = [] } = {}) => {
  const status = item.status || "Pending";
  const type = types.find((entry) => entry.id === item.expense_type_id) ||
    types.find((entry) => entry.name.trim().toLowerCase() === String(item.expense_type || "").trim().toLowerCase()) || null;
  return {
    id: item.id,
    requestId: item.request_id || claimRequestId(item.id),
    self,
    userId: item.user,
    employee: {
      id: item.user,
      name: item.user_name || "",
      code: item.employee_id || "",
      department: item.department_name || "",
      designation: item.designation || "",
      photo: item.image_url || "",
    },
    typeId: item.expense_type_id ?? type?.id ?? null,
    typeName: item.expense_type || type?.name || "",
    payslipLine: item.payslip_line || type?.payslipLine || "",
    purpose: item.description || "",
    comments: item.comments || "",
    paidBy: item.paid_by || "Employee",
    billDate: parseDay(item.bill_date || item.date_incurred),
    billNumber: item.bill_number || "",
    attachments: (item.attachments || []).map((file) => ({
      id: file.id,
      name: file.original_name || `bill-${file.id}`,
      url: file.url,
      contentType: file.content_type || "",
    })),
    amount: Number(item.amount || 0),
    amountApproved: toNumber(item.amount_approved),
    status,
    submittedAt: parseApiDate(item.applied_on),
    submittedBy: item.applied_by || "",
    approvedAt: parseApiDate(item.approvedat),
    approvedBy: item.approvedby || "",
    rejectedAt: parseApiDate(item.rejectedat),
    rejectedBy: item.rejectedby || "",
    rejectReason: status === "Rejected" ? item.reason || "" : "",
    cancelledAt: parseApiDate(item.cancelledat),
    cancelledBy: item.cancelledby || "",
    cancelReason: status === "Cancelled" ? item.reason || "" : "",
    payMonth: parsePayMonth(item.pay_month),
    addedToPayrollAt: parseApiDate(item.added_to_payroll_at),
    paidAt: parseApiDate(item.paid_at),
  };
};

// My claims: every page (100 a page) so the status cards, date range and
// monthly limit check see all of them.
export const fetchMyClaims = async ({ domain, companyId, userId, signal }) => {
  const rows = [];
  for (let page = 1; page <= 50; page += 1) {
    const data = await request(domain, "expense/", {
      query: { selected_user_id: userId, company_id: companyId, page, page_size: 100 },
      signal,
    });
    if (Array.isArray(data)) return data;
    rows.push(...(data?.results || []));
    if (!data?.next) break;
  }
  return rows;
};

export const fetchTeamClaims = async ({ domain, companyId, signal }) => {
  const data = await request(domain, TEAM_CLAIMS, { query: { company_id: companyId }, signal });
  return Array.isArray(data) ? data : data?.results || [];
};

// Submit a claim (multipart, bills as repeated "attachments"). bill_number is
// sent already; the server saves it once the field is added.
export const submitClaim = async ({ domain, companyId, values, files = [], approve = false }) => {
  const form = new FormData();
  const fields = {
    user: values.userId,
    expense_type: values.typeId,
    description: values.purpose,
    amount: Number(values.amount).toFixed(2),
    date_incurred: values.billDate,
    bill_date: values.billDate,
    bill_number: values.billNumber,
    paid_by: "Employee",
    ...(approve ? { action: "Approved" } : {}),
  };
  Object.entries(fields).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") form.append(key, value);
  });
  files.forEach((file) => form.append("attachments", file));
  return request(domain, "expense/", { method: "POST", query: { company_id: companyId }, form });
};

// Edit a Pending claim: PUT expense/{id}/ (same call the old Expense Claims
// edit used). JSON normally; multipart when a new bill photo is added.
export const updateClaim = ({ domain, companyId, id, values, files = [] }) => {
  const fields = {
    expense_type: values.typeId,
    description: values.purpose,
    amount: Number(values.amount).toFixed(2),
    date_incurred: values.billDate,
    bill_date: values.billDate,
    bill_number: values.billNumber,
    paid_by: "Employee",
  };
  const query = { company_id: companyId };
  if (!files.length) return request(domain, `expense/${id}/`, { method: "PUT", query, body: fields });
  const form = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    if (value !== undefined && value !== null) form.append(key, value);
  });
  files.forEach((file) => form.append("attachments", file));
  return request(domain, `expense/${id}/`, { method: "PUT", query, form });
};

export const cancelOwnClaim = ({ domain, companyId, id, reason }) =>
  request(domain, `expense/${id}/`, {
    method: "PUT",
    query: { company_id: companyId },
    body: { status: "Cancelled", ...(reason ? { reason } : {}) },
  });

// Team review (Approved / Rejected / Paid / Cancelled) of one or many claims.
// Returns { message, updated_ids, skipped_ids, not_found_ids }.
export const reviewClaims = ({ domain, companyId, ids, status, reason }) =>
  request(domain, TEAM_CLAIMS, {
    method: "PUT",
    query: { company_id: companyId },
    body: {
      ...(ids.length === 1 ? { id: Number(ids[0]) } : { ids: ids.map(Number) }),
      status,
      ...(reason ? { reason } : {}),
    },
  });

// Toast text for a review: changed claims, plus the ones the server skipped
// (only Pending claims, never the caller's own, can change today).
export const reviewOutcome = (result, verb) => {
  const updated = result?.updated_ids?.length || 0;
  const skipped = (result?.skipped_ids?.length || 0) + (result?.not_found_ids?.length || 0);
  if (!updated) {
    return { type: "error", text: `No claim was ${verb}. Only pending claims of other employees can be changed.` };
  }
  return {
    type: skipped ? "info" : "success",
    text: `${updated} claim(s) ${verb}.${skipped ? ` ${skipped} skipped (not pending or not found).` : ""}`,
  };
};

// Bills need the login token, so a plain link can't open them: fetch the
// file and open it in a new tab.
export const openClaimAttachment = async (url) => {
  const tab = window.open("", "_blank");
  try {
    const response = await fetch(url, { headers: { Authorization: `Token ${localStorage.getItem("token")}` } });
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw parseError(body, response.status);
    }
    const blobUrl = URL.createObjectURL(await response.blob());
    if (tab) tab.location.href = blobUrl;
    else window.open(blobUrl, "_blank");
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
  } catch (error) {
    tab?.close();
    throw error;
  }
};

// ---------- Payroll > Pay Inputs > Reimbursements (HR) ----------
// Screen statuses: PENDING (Pending + Returned), READY (Approved),
// PROCESSED (Added to Payroll + Paid), REJECTED. Cancelled claims are left
// out by the server. Sources: MY_WORKSPACE, MANUAL, IMPORT.

const payInputMoney = (value) => (value === null || value === undefined || value === "" ? null : Number(value));

export const fromApiPayInputClaim = (item) => ({
  id: item.id,
  requestNo: item.request_no || claimRequestId(item.id),
  billDate: item.bill_date || null,
  billNumber: item.bill_number || "",
  employee: {
    id: item.employee?.id ?? null,
    name: item.employee?.name || "",
    code: item.employee?.code || "",
    initials: item.employee?.initials || "",
    photo: item.employee?.photo_url || "",
    department: item.employee?.department || item.employee?.department_name || "",
    designation: item.employee?.designation || "",
  },
  typeId: item.type?.id ?? null,
  typeName: item.type?.name || "",
  description: item.description || "",
  amount: payInputMoney(item.amount) ?? 0,
  amountApproved: payInputMoney(item.amount_approved),
  sourceCode: item.source?.code || "",
  sourceLabel: item.source?.label || item.source?.code || "",
  statusCode: item.status?.code || "",
  statusLabel: item.status?.label || item.status?.code || "",
  claimStatus: item.status?.claim_status || "",
  payrollCycle: item.payroll_cycle
    ? { id: item.payroll_cycle.id, name: item.payroll_cycle.name || "", expected: Boolean(item.payroll_cycle.is_expected) }
    : null,
  hasBill: Boolean(item.has_bill),
  canEdit: Boolean(item.can_edit),
  canCancel: Boolean(item.can_cancel),
  // Detail only (endpoint 5).
  comments: item.comments || "",
  reason: item.reason || "",
  attachments: (item.attachments || []).map((file) => ({
    id: file.id,
    name: file.original_name || `bill-${file.id}`,
    url: file.url,
    contentType: file.content_type || "",
    size: file.size ?? null,
  })),
  timeline: (item.timeline || []).map((step) => ({
    step: step.step,
    stageNo: step.stage_no ?? null,
    label: step.label || "",
    status: step.status || "",
    by: step.by || step.approver || "",
    amount: payInputMoney(step.amount),
    remarks: step.remarks || "",
    at: step.at || null,
  })),
  createdBy: item.created_by || "",
  createdAt: item.created_at || null,
});

const toCard = (value) => ({ amount: Number(value?.amount || 0), count: Number(value?.count || 0) });

// 1. Count cards (whole company unless filters are passed).
export const fetchPayInputSummary = async ({ domain, companyId, payScheduleId, typeId, search, signal }) => {
  const data = await request(domain, `${PAY_INPUT_BASE}summary/`, {
    query: { company_id: companyId, pay_schedule_id: payScheduleId, expense_type_id: typeId, search },
    signal,
  });
  return {
    total: toCard(data?.total),
    pending: toCard(data?.pending),
    ready: toCard(data?.ready),
    processed: toCard(data?.processed),
    rejected: toCard(data?.rejected),
  };
};

// 2. Claims table: every page (200 a page, max 50 pages) so the standard
// list toolbar can search, filter, sort and export them on the screen.
export const fetchPayInputClaims = async ({ domain, companyId, signal }) => {
  const rows = [];
  for (let page = 1; page <= 50; page += 1) {
    const data = await request(domain, `${PAY_INPUT_BASE}claims/`, {
      query: { company_id: companyId, page, page_size: 200 },
      signal,
    });
    if (Array.isArray(data)) return data.map(fromApiPayInputClaim);
    rows.push(...(data?.results || []));
    if (!data?.next) break;
  }
  return rows.map(fromApiPayInputClaim);
};

// 5. Claim detail (bills, comments, timeline).
export const fetchPayInputClaim = async ({ domain, companyId, id, signal }) =>
  fromApiPayInputClaim(await request(domain, `${PAY_INPUT_BASE}claims/${id}/`, { query: { company_id: companyId }, signal }));

const API_FIELD = {
  employeeId: "employee_id",
  typeId: "expense_type_id",
  billDate: "bill_date",
  billNumber: "bill_number",
  amount: "amount",
  description: "description",
  comments: "comments",
};
const toApiValue = (key, value) => (key === "amount" && value !== "" && value != null ? Number(value).toFixed(2) : value);

// 4. + Add Reimbursement (source MANUAL). Multipart when bills are attached.
export const createPayInputClaim = async ({ domain, companyId, values, files = [] }) => {
  const fields = { company_id: Number(companyId) };
  Object.entries(API_FIELD).forEach(([key, apiKey]) => {
    const value = toApiValue(key, values[key]);
    if (value !== undefined && value !== null && value !== "") fields[apiKey] = value;
  });
  if (!files.length) {
    return fromApiPayInputClaim(await request(domain, `${PAY_INPUT_BASE}claims/`, { method: "POST", body: fields }));
  }
  const form = new FormData();
  Object.entries(fields).forEach(([key, value]) => form.append(key, value));
  files.forEach((file) => form.append("attachments[]", file));
  return fromApiPayInputClaim(await request(domain, `${PAY_INPUT_BASE}claims/`, { method: "POST", form }));
};

// 6. Edit a Pending Manual / Import claim: only the changed fields are sent.
export const updatePayInputClaim = async ({ domain, companyId, id, changes }) => {
  const body = { company_id: Number(companyId) };
  Object.entries(changes).forEach(([key, value]) => {
    if (API_FIELD[key]) body[API_FIELD[key]] = value === "" ? null : toApiValue(key, value);
  });
  return fromApiPayInputClaim(await request(domain, `${PAY_INPUT_BASE}claims/${id}/`, { method: "PATCH", body }));
};

// 7. Cancel a Pending Manual / Import claim.
export const cancelPayInputClaim = ({ domain, companyId, id, remarks }) =>
  request(domain, `${PAY_INPUT_BASE}claims/${id}/cancel/`, {
    method: "POST",
    body: { company_id: Number(companyId), ...(remarks ? { remarks } : {}) },
  });

// 8. Import template (.xlsx from the server, with the company's types).
export const downloadPayInputTemplate = async ({ domain, companyId }) => {
  const response = await fetch(buildUrl(domain, `${PAY_INPUT_BASE}import/template/`, { company_id: companyId }), {
    headers: { Authorization: `Token ${localStorage.getItem("token")}` },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw parseError(body, response.status);
  }
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement("a");
  link.href = url;
  link.download = "reimbursement_import_template.xlsx";
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

const importForm = (companyId, file, importKey) => {
  const form = new FormData();
  form.append("company_id", companyId);
  if (importKey) form.append("import_key", importKey);
  form.append("file", file);
  return form;
};

// 9. Import: check the file (nothing saved).
export const checkPayInputImport = async ({ domain, companyId, file }) => {
  const data = await request(domain, `${PAY_INPUT_BASE}import/`, {
    method: "POST",
    query: { dry_run: "true" },
    form: importForm(companyId, file),
  });
  return {
    importKey: data?.import_key || "",
    total: Number(data?.total || 0),
    valid: Number(data?.valid || 0),
    invalid: Number(data?.invalid || 0),
    validAmount: Number(data?.valid_amount || 0),
    rows: (data?.rows || []).map((row) => ({
      row: row.row,
      employeeCode: row.employee_code || "",
      name: row.name || "",
      type: row.type || "",
      billDate: row.bill_date || "",
      amount: row.amount,
      ok: row.result === "OK",
      errors: (row.errors || []).map((error) => error.message || error.code).filter(Boolean),
    })),
  };
};

// 10. Import: save the valid rows (same file + import_key).
export const savePayInputImport = async ({ domain, companyId, file, importKey }) => {
  const data = await request(domain, `${PAY_INPUT_BASE}import/`, { method: "POST", form: importForm(companyId, file, importKey) });
  return {
    created: Number(data?.created || 0),
    skipped: Number(data?.skipped || 0),
    amount: Number(data?.amount || 0),
    message: data?.message || "",
  };
};

// Active employees of the company for the Add form. The claim API's
// employee_id is the user id (same as employee.id on the rows).
export const fetchPayInputEmployees = async ({ domain, companyId, signal }) => {
  const data = await request(domain, "list/employee", {
    query: { company_id: companyId, department_id: 99999, status: "Active" },
    signal,
  });
  const list = Array.isArray(data) ? data : data?.results || [];
  return list
    .filter((employee) => employee.user_id != null)
    .map((employee) => ({
      id: Number(employee.user_id),
      code: employee.employee_id ? String(employee.employee_id) : "",
      name: employee.user_name || employee.username || employee.employee_name || String(employee.employee_id || employee.user_id),
      photo: employee.image_url || "",
      department: employee.department_name || employee.department || "",
      designation: employee.designation_name || employee.designation || "",
    }));
};

// Bill links in the detail may be relative ("/api/expense-attachment/88/").
export const resolveAttachmentUrl = (domain, url) => {
  if (!url) return url;
  try {
    return new URL(url, String(domain || window.location.origin)).href;
  } catch (error) {
    return url;
  }
};
